import { ClockCircleOutlined, FireOutlined, WarningOutlined } from "@ant-design/icons";
import { Flex } from "antd";
import type { CSSProperties, ReactNode } from "react";

// ─── Types ───────────────────────────────────────────────────────────────────

export type Severity = "critical" | "high" | "medium" | "low";
export type Status = "open" | "in_review" | "addressed" | "closed" | "false_positive";

export type Finding = {
  id: number;
  rule_id: string;
  rule_name: string;
  severity: Severity;
  status: Status;
  judgment: "fail" | "partial";
  rationale: string;
  clinician_id: number;
  clinician_name: string;
  client_initials: string;
  note_id: number;
  note_title: string;
  note_type: string;
  assigned_to: string | null;
  created_at: string;
  last_status_change_at: string;
  comments: { author: string; at: string; body: string }[];
  is_archived: boolean;
};

// ─── Visual config ───────────────────────────────────────────────────────────

// Single source of truth for every semantic color in the compliance area.
// All banners, tags, table cells, trend arrows, and capture bars pull from
// here, so the same meaning always reads the same color instead of each file
// hand-picking its own near-duplicate red/amber/green.
//   fg     = text       bg     = fill
//   border = hairline   accent = icon / dot / chart fill
export type Tone = "danger" | "warning" | "success" | "info" | "neutral";

export const TONE: Record<Tone, { fg: string; bg: string; border: string; accent: string }> = {
  danger: { fg: "#b91c1c", bg: "#fef2f2", border: "#fecaca", accent: "#dc2626" },
  warning: { fg: "#b45309", bg: "#fffbeb", border: "#fde68a", accent: "#d97706" },
  success: { fg: "#047857", bg: "#ecfdf5", border: "#a7f3d0", accent: "#10b981" },
  info: { fg: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe", accent: "#3b82f6" },
  neutral: { fg: "#374151", bg: "#f9fafb", border: "#e5e7eb", accent: "#9ca3af" },
};

// Severity is a 4-step scale (critical → low). Critical/medium/low reuse the
// danger/warning/neutral tones; high keeps a distinct orange step in between.
export const SEVERITY_META: Record<
  Severity,
  { label: string; color: string; bg: string; border: string; icon: ReactNode }
> = {
  critical: {
    label: "Critical",
    color: TONE.danger.fg,
    bg: TONE.danger.bg,
    border: TONE.danger.border,
    icon: <FireOutlined />,
  },
  high: { label: "High", color: "#c2410c", bg: "#fff7ed", border: "#fed7aa", icon: <WarningOutlined /> },
  medium: {
    label: "Medium",
    color: TONE.warning.fg,
    bg: TONE.warning.bg,
    border: TONE.warning.border,
    icon: <WarningOutlined />,
  },
  low: {
    label: "Low",
    color: TONE.neutral.fg,
    bg: "#f3f4f6",
    border: TONE.neutral.border,
    icon: <ClockCircleOutlined />,
  },
};

export const STATUS_META: Record<Status, { label: string; color: string; bg: string; dot: string }> = {
  open: { label: "Open", color: TONE.danger.fg, bg: TONE.danger.bg, dot: TONE.danger.accent },
  in_review: { label: "In review", color: TONE.info.fg, bg: TONE.info.bg, dot: TONE.info.accent },
  addressed: { label: "Addressed", color: TONE.warning.fg, bg: TONE.warning.bg, dot: TONE.warning.accent },
  closed: { label: "Closed", color: TONE.success.fg, bg: TONE.success.bg, dot: TONE.success.accent },
  false_positive: { label: "False positive", color: TONE.neutral.fg, bg: TONE.neutral.bg, dot: TONE.neutral.accent },
};

export const SLA_DAYS: Record<Severity, number> = { critical: 1, high: 3, medium: 7, low: 14 };

export const SEVERITY_ORDER: Severity[] = ["critical", "high", "medium", "low"];

export const ASSIGNEES = ["Saureen Shah", "Lena Park", "Andre Cole", "Priya Singh"];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const today = new Date();
const daysAgoISO = (n: number) => new Date(today.getTime() - n * 24 * 60 * 60 * 1000).toISOString();

export const ageDays = (isoDate: string) =>
  Math.max(0, Math.floor((Date.now() - new Date(isoDate).getTime()) / 86400000));

export const ageLabel = (d: number) => {
  if (d < 1) return "Today";
  if (d === 1) return "1 day";
  if (d < 7) return `${d} days`;
  if (d < 14) return "1 week";
  if (d < 30) return `${Math.floor(d / 7)} weeks`;
  return `${Math.floor(d / 30)}mo`;
};

// ─── Tag components ──────────────────────────────────────────────────────────

export const SeverityTag = ({ severity }: { severity: Severity }) => {
  const m = SEVERITY_META[severity];
  return (
    <Flex
      align="center"
      gap={4}
      style={{
        background: m.bg,
        color: m.color,
        border: `1px solid ${m.border}`,
        padding: "2px 8px",
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        width: "fit-content",
      }}
    >
      {m.icon}
      <span>{m.label}</span>
    </Flex>
  );
};

export const StatusTag = ({ status }: { status: Status }) => {
  const m = STATUS_META[status];
  return (
    <Flex
      align="center"
      gap={5}
      style={{
        background: m.bg,
        color: m.color,
        padding: "3px 9px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        width: "fit-content",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 999, background: m.dot, display: "inline-block" }} />
      {m.label}
    </Flex>
  );
};

// Standard inline alert banner for the compliance area (critical findings,
// warnings, etc.). Colors come from TONE so every banner — whatever the tone —
// shares the same text/fill/border/accent treatment.
export const ComplianceBanner = ({
  tone,
  icon,
  action,
  children,
  style,
}: {
  tone: Tone;
  icon?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  style?: CSSProperties;
}) => {
  const t = TONE[tone];
  return (
    <Flex
      align="center"
      gap={10}
      wrap="wrap"
      style={{
        background: t.bg,
        border: `1px solid ${t.border}`,
        borderRadius: 8,
        padding: "12px 16px",
        ...style,
      }}
    >
      {icon ? <span style={{ color: t.accent, fontSize: 18, display: "inline-flex" }}>{icon}</span> : null}
      <span style={{ flex: "1 1 200px", color: t.fg, fontSize: 13 }}>{children}</span>
      {action ? <span style={{ flexShrink: 0 }}>{action}</span> : null}
    </Flex>
  );
};

// ─── Mock data ───────────────────────────────────────────────────────────────

export const MOCK_FINDINGS: Finding[] = [
  {
    id: 1,
    rule_id: "risk.ssi.1",
    rule_name: "Suicidal ideation requires C-SSRS + safety plan",
    severity: "critical",
    status: "open",
    judgment: "fail",
    rationale:
      "Client disclosed passive SI in the session ('sometimes I wish I'd just disappear'). No Columbia Suicide Severity Rating Scale (C-SSRS) score is documented and no safety planning is recorded in the note.",
    clinician_id: 42,
    clinician_name: "Dr. Rachel Lim",
    client_initials: "M.K.",
    note_id: 30182,
    note_title: "Follow-up · 5/19",
    note_type: "Therapy Followup",
    assigned_to: null,
    created_at: daysAgoISO(0.5),
    last_status_change_at: daysAgoISO(0.5),
    comments: [],
    is_archived: false,
  },
  {
    id: 2,
    rule_id: "risk.ssi.1",
    rule_name: "Suicidal ideation requires C-SSRS + safety plan",
    severity: "critical",
    status: "in_review",
    judgment: "fail",
    rationale:
      "Note references 'recent thoughts of self-harm' under HPI. The assessment field does not include a C-SSRS score and the plan does not reference a safety plan or means restriction discussion.",
    clinician_id: 88,
    clinician_name: "Dr. Jamie Cortez",
    client_initials: "R.P.",
    note_id: 30099,
    note_title: "Intake · 5/16",
    note_type: "Psychiatric Intake",
    assigned_to: "Saureen Shah",
    created_at: daysAgoISO(4),
    last_status_change_at: daysAgoISO(1),
    comments: [
      { author: "Saureen Shah", at: daysAgoISO(1), body: "Reviewing. Will follow up with Dr. Cortez tomorrow." },
    ],
    is_archived: false,
  },
  {
    id: 3,
    rule_id: "coding.90837.duration",
    rule_name: "CPT 90837 requires ≥53 min psychotherapy",
    severity: "high",
    status: "open",
    judgment: "fail",
    rationale:
      "Note coded as 90837 (psychotherapy 60 minutes). The session duration documented is 38 minutes. 90834 (45 minutes) would be the appropriate code; this is a payer-audit exposure.",
    clinician_id: 42,
    clinician_name: "Dr. Rachel Lim",
    client_initials: "T.S.",
    note_id: 30144,
    note_title: "Follow-up · 5/17",
    note_type: "Therapy Followup",
    assigned_to: null,
    created_at: daysAgoISO(3),
    last_status_change_at: daysAgoISO(3),
    comments: [],
    is_archived: false,
  },
  {
    id: 4,
    rule_id: "screening.phq9.90day",
    rule_name: "PHQ-9 score required every 90 days for MDD patients",
    severity: "medium",
    status: "open",
    judgment: "partial",
    rationale:
      "Client carries an active MDD diagnosis. Last documented PHQ-9 score is from 102 days ago. Screening cadence has lapsed beyond the 90-day window.",
    clinician_id: 51,
    clinician_name: "Dr. Marcus Howell",
    client_initials: "L.W.",
    note_id: 30121,
    note_title: "Follow-up · 5/15",
    note_type: "Therapy Followup",
    assigned_to: "Lena Park",
    created_at: daysAgoISO(6),
    last_status_change_at: daysAgoISO(2),
    comments: [
      {
        author: "Lena Park",
        at: daysAgoISO(2),
        body: "Reached out to Dr. Howell. PHQ-9 will be administered next visit.",
      },
    ],
    is_archived: false,
  },
  {
    id: 5,
    rule_id: "treatment.plan.review",
    rule_name: "Treatment plan review required every 6 months",
    severity: "medium",
    status: "addressed",
    judgment: "partial",
    rationale:
      "Treatment plan was last formally reviewed 8 months ago. No documentation of formal review or update in subsequent notes.",
    clinician_id: 88,
    clinician_name: "Dr. Jamie Cortez",
    client_initials: "J.D.",
    note_id: 30077,
    note_title: "Follow-up · 5/12",
    note_type: "Therapy Followup",
    assigned_to: "Saureen Shah",
    created_at: daysAgoISO(11),
    last_status_change_at: daysAgoISO(2),
    comments: [
      {
        author: "Saureen Shah",
        at: daysAgoISO(3),
        body: "Coaching note delivered. Tx plan review scheduled for next session.",
      },
      { author: "Dr. Jamie Cortez", at: daysAgoISO(2), body: "Confirmed — added to my calendar for 5/26." },
    ],
    is_archived: false,
  },
  {
    id: 6,
    rule_id: "documentation.medical_necessity",
    rule_name: "Medical necessity statement required",
    severity: "high",
    status: "open",
    judgment: "fail",
    rationale:
      "No explicit medical necessity statement in the assessment or plan fields. Payer audits routinely cite this as a denial reason.",
    clinician_id: 119,
    clinician_name: "Dr. Priya Iyer",
    client_initials: "C.B.",
    note_id: 30150,
    note_title: "Follow-up · 5/17",
    note_type: "Therapy Followup",
    assigned_to: null,
    created_at: daysAgoISO(5),
    last_status_change_at: daysAgoISO(5),
    comments: [],
    is_archived: false,
  },
  {
    id: 7,
    rule_id: "risk.hi.1",
    rule_name: "Homicidal ideation requires risk assessment + Tarasoff review",
    severity: "critical",
    status: "open",
    judgment: "fail",
    rationale:
      "Note references 'thoughts of harming a coworker' in the subjective field. No formal risk assessment, no Tarasoff consideration, no documentation of duty to warn analysis.",
    clinician_id: 51,
    clinician_name: "Dr. Marcus Howell",
    client_initials: "A.G.",
    note_id: 30201,
    note_title: "Intake · 5/20",
    note_type: "Psychiatric Intake",
    assigned_to: null,
    created_at: daysAgoISO(1),
    last_status_change_at: daysAgoISO(1),
    comments: [],
    is_archived: false,
  },
  {
    id: 8,
    rule_id: "coding.telehealth.modifier",
    rule_name: "Telehealth notes require 95 modifier + POS 10/02",
    severity: "medium",
    status: "closed",
    judgment: "fail",
    rationale: "Note documents telehealth delivery but billing code lacks the 95 modifier.",
    clinician_id: 42,
    clinician_name: "Dr. Rachel Lim",
    client_initials: "K.R.",
    note_id: 30050,
    note_title: "Follow-up · 5/9",
    note_type: "Therapy Followup",
    assigned_to: "Lena Park",
    created_at: daysAgoISO(14),
    last_status_change_at: daysAgoISO(7),
    comments: [
      { author: "Lena Park", at: daysAgoISO(8), body: "Billing team corrected the claim. Confirmed remediated." },
    ],
    is_archived: false,
  },
  {
    id: 9,
    rule_id: "screening.audit.alc",
    rule_name: "AUDIT-C documented annually for substance-use diagnoses",
    severity: "low",
    status: "open",
    judgment: "partial",
    rationale: "No AUDIT-C score on file in the past 12 months despite active SUD diagnosis.",
    clinician_id: 119,
    clinician_name: "Dr. Priya Iyer",
    client_initials: "D.F.",
    note_id: 30088,
    note_title: "Follow-up · 5/13",
    note_type: "Therapy Followup",
    assigned_to: null,
    created_at: daysAgoISO(9),
    last_status_change_at: daysAgoISO(9),
    comments: [],
    is_archived: true,
  },
  {
    id: 10,
    rule_id: "documentation.signature",
    rule_name: "Notes must be signed within 72 hours",
    severity: "low",
    status: "false_positive",
    judgment: "partial",
    rationale: "Note signed 80 hours after session.",
    clinician_id: 88,
    clinician_name: "Dr. Jamie Cortez",
    client_initials: "H.M.",
    note_id: 30033,
    note_title: "Follow-up · 5/8",
    note_type: "Therapy Followup",
    assigned_to: "Saureen Shah",
    created_at: daysAgoISO(15),
    last_status_change_at: daysAgoISO(12),
    comments: [
      { author: "Saureen Shah", at: daysAgoISO(12), body: "Dr. Cortez was on approved leave. Marking false positive." },
    ],
    is_archived: false,
  },
  {
    id: 11,
    rule_id: "risk.ssi.2",
    rule_name: "Means restriction discussion required when SI present",
    severity: "high",
    status: "in_review",
    judgment: "partial",
    rationale: "C-SSRS documented but no means restriction conversation evidenced in plan.",
    clinician_id: 42,
    clinician_name: "Dr. Rachel Lim",
    client_initials: "M.K.",
    note_id: 30182,
    note_title: "Follow-up · 5/19",
    note_type: "Therapy Followup",
    assigned_to: "Saureen Shah",
    created_at: daysAgoISO(2),
    last_status_change_at: daysAgoISO(1),
    comments: [],
    is_archived: false,
  },
  {
    id: 12,
    rule_id: "treatment.goals.measurable",
    rule_name: "Treatment goals must be measurable and time-bound",
    severity: "low",
    status: "open",
    judgment: "partial",
    rationale: "Listed goal 'reduce anxiety' has no measurable target or time-frame.",
    clinician_id: 51,
    clinician_name: "Dr. Marcus Howell",
    client_initials: "N.O.",
    note_id: 30164,
    note_title: "Intake · 5/18",
    note_type: "Psychiatric Intake",
    assigned_to: null,
    created_at: daysAgoISO(4),
    last_status_change_at: daysAgoISO(4),
    comments: [],
    is_archived: true,
  },
  {
    id: 13,
    rule_id: "documentation.medical_necessity",
    rule_name: "Medical necessity statement required",
    severity: "high",
    status: "open",
    judgment: "fail",
    rationale: "Medical necessity statement absent. Repeat issue for this clinician (4th in 30 days).",
    clinician_id: 88,
    clinician_name: "Dr. Jamie Cortez",
    client_initials: "F.V.",
    note_id: 30172,
    note_title: "Follow-up · 5/19",
    note_type: "Therapy Followup",
    assigned_to: null,
    created_at: daysAgoISO(2),
    last_status_change_at: daysAgoISO(2),
    comments: [],
    is_archived: false,
  },
  {
    id: 14,
    rule_id: "coding.90837.duration",
    rule_name: "CPT 90837 requires ≥53 min psychotherapy",
    severity: "high",
    status: "in_review",
    judgment: "fail",
    rationale: "Note coded 90837 but session was 42 minutes.",
    clinician_id: 119,
    clinician_name: "Dr. Priya Iyer",
    client_initials: "B.L.",
    note_id: 30106,
    note_title: "Follow-up · 5/14",
    note_type: "Therapy Followup",
    assigned_to: "Lena Park",
    created_at: daysAgoISO(8),
    last_status_change_at: daysAgoISO(3),
    comments: [],
    is_archived: false,
  },
  {
    id: 15,
    rule_id: "screening.phq9.90day",
    rule_name: "PHQ-9 score required every 90 days for MDD patients",
    severity: "medium",
    status: "open",
    judgment: "partial",
    rationale: "PHQ-9 lapsed by 14 days for an active MDD patient.",
    clinician_id: 42,
    clinician_name: "Dr. Rachel Lim",
    client_initials: "E.K.",
    note_id: 30110,
    note_title: "Follow-up · 5/15",
    note_type: "Therapy Followup",
    assigned_to: null,
    created_at: daysAgoISO(7),
    last_status_change_at: daysAgoISO(7),
    comments: [],
    is_archived: false,
  },
];
